import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconArrowUpRight, IconArrowDownRight, IconMinus } from '@tabler/icons-react';
import styles from './CockpitKpiBand.module.scss';
import { useProjectIntelligence } from '../../hooks/useProjectIntelligence';
import {
  KPI,
  categorical,
  formatDelta,
  formatPercent,
  healthKeyFromStatus,
  indexProjectKpis,
  lastComputedAt,
  numeric,
} from '../ReportMetrics/intelligence';
import { getProjectHealthStatus } from '../ReportMetrics/reportData';
import { series, status as statusColors } from '../ReportMetrics/reportTheme';
import ScoreRing from '../ReportMetrics/widgets/ScoreRing';
import LastComputed from '../ReportMetrics/widgets/LastComputed';
import { actionSummary } from '../ReportMetrics/detail';
import KpiSidePanel, { PanelKind } from './KpiSidePanel';

interface CockpitKpiBandProps {
  projectId?: number;
}

/**
 * The KPI band across the top of the Cockpit (spec §17): project health and the
 * four dimension scores, the 7-day trend, open action points, and how many
 * phases and deliverables are overdue.
 *
 * Reads the same engine values as the Project Intelligence report, so the two
 * cannot show different numbers for the same KPI. It asks for current values
 * and Action Points only - no history, graph or milestones. Every tile opens
 * its KPI side panel, which loads the rest when it is opened.
 */
const CockpitKpiBand: React.FC<CockpitKpiBandProps> = ({ projectId }) => {
  const { t } = useTranslation(['templates', 'gantt']);
  const { kpis, actionPoints, isLoading } = useProjectIntelligence(projectId, {
    history: false,
    graph: false,
    milestones: false,
    findings: false,
  });
  const [panel, setPanel] = useState<PanelKind | null>(null);
  // Same count as the Übersicht's "open action points": the project's Action
  // Points, not the rule findings, which the side panel lists separately.
  const openActions = useMemo(() => actionSummary(actionPoints).open, [actionPoints]);

  const index = useMemo(() => indexProjectKpis(kpis), [kpis]);
  const computedAt = useMemo(() => lastComputedAt(kpis), [kpis]);

  if (isLoading || (kpis?.length ?? 0) === 0) {
    // Silent on an empty project: the Gantt below is still perfectly usable,
    // and the band fills in by itself on the next recompute.
    return null;
  }

  const healthScore = numeric(index, KPI.projectHealthScore);
  const healthKey =
    healthKeyFromStatus(categorical(index, KPI.projectHealthStatus)) ??
    (healthScore != null ? getProjectHealthStatus(healthScore).key : undefined);
  const healthColor =
    healthScore != null ? getProjectHealthStatus(healthScore).color : statusColors.warning;

  const trend = formatDelta(numeric(index, KPI.projectHealthTrend));
  // no 7-day-old snapshot yet: a dash says it, an arrow icon beside it does not
  const trendKnown = numeric(index, KPI.projectHealthTrend) != null;
  const TrendIcon =
    trend.direction === 'up'
      ? IconArrowUpRight
      : trend.direction === 'down'
        ? IconArrowDownRight
        : IconMinus;
  const trendClass =
    trend.direction === 'up'
      ? styles.deltaGood
      : trend.direction === 'down'
        ? styles.deltaBad
        : styles.deltaFlat;

  const overduePhases = numeric(index, KPI.overduePhaseCount);
  const overdueDeliverables = numeric(index, KPI.overdueDeliverableCount);
  const count = (value?: number) => (typeof value === 'number' ? `${Math.round(value)}` : '—');
  const criticalIf = (value?: number) =>
    `${styles.stat} ${value != null && value > 0 ? styles.statCritical : ''}`;

  const tileClass = (kind: PanelKind) => `${styles.tile} ${panel === kind ? styles.tileActive : ''}`;
  // A second click on the open tile closes its panel (spec §2).
  const toggle = (kind: PanelKind) => setPanel((open) => (open === kind ? null : kind));

  const dimension = (kind: PanelKind, labelKey: string, key: string, sub?: React.ReactNode) => (
    <button type="button" className={tileClass(kind)} onClick={() => toggle(kind)}>
      <span className={styles.label}>{t(`report.dimensions.${labelKey}`)}</span>
      <div className={styles.ringRow}>
        <ScoreRing value={numeric(index, key)} color={series.blue} />
      </div>
      {sub}
    </button>
  );

  return (
    <>
    <div className={styles.band}>
      <button type="button" className={tileClass('health')} onClick={() => toggle('health')}>
        <span className={styles.label}>{t('report.health.title')}</span>
        <div className={styles.ringRow}>
          <ScoreRing value={healthScore} color={healthColor} />
        </div>
        {healthKey && (
          <span
            className={styles.pill}
            style={{ color: healthColor, background: `${healthColor}1a` }}
          >
            {t(`report.health.status.${healthKey}`)}
          </span>
        )}
      </button>

      <div className={`${styles.tile} ${panel === 'actions' ? styles.tileActive : ''}`}>
        <button type="button" className={styles.subButton} onClick={() => toggle('health')}>
          <span className={styles.label}>{t('report.cockpit.trend')}</span>
          <div className={styles.statRow}>
            <span className={`${styles.delta} ${trendClass}`}>
              {trendKnown && <TrendIcon size={14} stroke={2} />}
              {trend.delta}
            </span>
          </div>
        </button>
        <button type="button" className={styles.subButton} onClick={() => toggle('actions')}>
          <span className={styles.label}>{t('report.cockpit.actionPoints')}</span>
          <div className={styles.statRow}>
            <span className={criticalIf(openActions)}>{openActions}</span>
            <span className={styles.sub}>{t('report.cockpit.open')}</span>
          </div>
        </button>
      </div>

      {dimension('readiness', 'readiness', KPI.readinessScore)}
      {dimension('execution', 'execution', KPI.executionScore)}
      {dimension(
        'quality',
        'quality',
        KPI.qualityScore,
        <span className={styles.sub}>
          {t('report.cockpit.timeliness', { value: formatPercent(numeric(index, KPI.qaTimeliness)) })}
        </span>,
      )}
      {dimension(
        'dependency',
        'dependency',
        KPI.dependencyScore,
        <span className={styles.sub}>
          {t('report.cockpit.integrity', {
            value: formatPercent(numeric(index, KPI.dependencyIntegrity)),
          })}
        </span>,
      )}

      <button type="button" className={tileClass('progress')} onClick={() => toggle('progress')}>
        <span className={styles.label}>{t('report.cockpit.overduePhases')}</span>
        <div className={styles.statRow}>
          <span className={criticalIf(overduePhases)}>{count(overduePhases)}</span>
        </div>
        <span className={styles.label}>{t('report.cockpit.overdueDeliverables')}</span>
        <div className={styles.statRow}>
          <span className={criticalIf(overdueDeliverables)}>{count(overdueDeliverables)}</span>
        </div>
      </button>
    </div>
    {/* the band reads a queued recompute's output, so it says how old that is */}
    <div className={styles.freshness}>
      <LastComputed at={computedAt} />
    </div>
    {panel && <KpiSidePanel projectId={projectId} kind={panel} onClose={() => setPanel(null)} />}
    </>
  );
};

export default CockpitKpiBand;
